import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { QUOTE_STATUSES } from "@/lib/constants";
import { Panel, Empty, AdminSelect, StatusBadge } from "@/pages/admin/adminui";
import { LuxButton } from "@/components/lux";

export function QuotesTab() {
  const [quotes, setQuotes] = useState([]);
  const [active, setActive] = useState(null);
  const [status, setStatus] = useState("");
  const load = () => api.get("/admin/quotes").then((r) => setQuotes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (active) setStatus(active.status); }, [active]);

  const save = async () => {
    await api.patch(`/admin/quotes/${active.id}`, { status });
    toast.success("Quote updated"); setActive(null); load();
  };
  const del = async (id) => { if (!window.confirm("Delete quote?")) return; await api.delete(`/admin/quotes/${id}`); load(); };

  return (
    <Panel testid="quotes-tab" title="Custom Quote Requests">
      {quotes.length === 0 ? <Empty>No quote requests yet.</Empty> : (
        <div className="space-y-3">
          {quotes.map((qt) => (
            <div key={qt.id} className="rounded-xl border border-white/10 bg-[#0b0d12] p-4 flex items-center justify-between gap-3" data-testid={`quote-${qt.reference}`}>
              <button onClick={() => setActive(qt)} className="text-left flex-1">
                <p className="font-mono2 text-[#02AACF] text-xs">{qt.reference}</p>
                <p className="text-white font-semibold">{qt.full_name} · <span className="text-[#94A3B8] font-normal">{qt.project_type}</span></p>
                <p className="text-[#64748B] text-xs mt-0.5 line-clamp-1">{qt.details}</p>
              </button>
              <div className="flex items-center gap-3 shrink-0">
                <StatusBadge status={qt.status} />
                <button onClick={() => del(qt.id)} className="text-[#64748B] hover:text-[#f87171]" data-testid={`delete-quote-${qt.reference}`}><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#12141C] border-white/10 text-white max-w-lg" data-testid="quote-detail">
          <DialogTitle className="sr-only">Quote details</DialogTitle>
          {active && (<>
            <p className="font-mono2 text-[#02AACF] text-xs">{active.reference}</p>
            <h3 className="font-display font-bold text-xl">{active.full_name}</h3>
            <div className="text-sm text-[#cbd5e1] space-y-1 mt-2">
              <p>{active.email} · {active.phone}</p>
              <p className="text-[#94A3B8]">{active.project_type} · Budget: {active.budget || "—"} · {active.preferred_date || "flexible"}</p>
              <p className="mt-2">{active.details}</p>
            </div>
            <div className="mt-4"><AdminSelect label="Status" value={status} onChange={(e) => setStatus(e.target.value)} options={QUOTE_STATUSES} testid="quote-status" /></div>
            <LuxButton className="mt-5 w-full" onClick={save} data-testid="save-quote">Save</LuxButton>
          </>)}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

export function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => { api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  return (
    <Panel testid="clients-tab" title="Client Database">
      {clients.length === 0 ? <Empty>No clients yet.</Empty> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {clients.map((c) => (
            <button key={c.id} onClick={() => setActive(c)} data-testid={`client-${c.id}`}
              className="text-left rounded-xl border border-white/10 bg-[#0b0d12] p-4 hover:border-[#02AACF]/50 transition-all">
              <p className="text-white font-semibold">{c.name}</p>
              <p className="text-[#64748B] text-xs">{c.email}</p>
              <p className="text-[#02AACF] text-xs font-mono2 mt-2">{c.total_bookings} booking(s)</p>
            </button>
          ))}
        </div>
      )}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#12141C] border-white/10 text-white max-w-lg" data-testid="client-detail">
          <DialogTitle className="sr-only">Client details</DialogTitle>
          {active && (<>
            <h3 className="font-display font-bold text-xl">{active.name}</h3>
            <p className="text-[#94A3B8] text-sm">{active.email} · {active.phone}</p>
            {active.instagram && <p className="text-[#94A3B8] text-sm">IG: {active.instagram}</p>}
            {active.company && <p className="text-[#94A3B8] text-sm">{active.company}</p>}
            <p className="font-mono2 text-xs text-[#64748B] uppercase mt-5 mb-2">Booking history</p>
            <div className="space-y-2">
              {(active.bookings || []).map((b) => (
                <div key={b.reference} className="flex justify-between rounded-lg border border-white/10 p-3 text-sm">
                  <span className="text-[#02AACF] font-mono2 text-xs">{b.reference}</span>
                  <span className="text-[#cbd5e1]">{b.package_name}</span>
                  <span className="text-[#94A3B8]">{b.shoot_date}</span>
                  <StatusBadge status={b.status} />
                </div>
              ))}
              {(!active.bookings || active.bookings.length === 0) && <p className="text-[#64748B] text-sm">No bookings.</p>}
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

export function MessagesTab() {
  const [msgs, setMsgs] = useState([]);
  const load = () => api.get("/admin/messages").then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const markRead = async (m) => { if (!m.read) { await api.patch(`/admin/messages/${m.id}`); load(); } };
  const del = async (id) => { if (!window.confirm("Delete message?")) return; await api.delete(`/admin/messages/${id}`); load(); };
  return (
    <Panel testid="messages-tab" title="Contact Messages">
      {msgs.length === 0 ? <Empty>No messages yet.</Empty> : (
        <div className="space-y-3">
          {msgs.map((m) => (
            <div key={m.id} onClick={() => markRead(m)} data-testid={`message-${m.id}`}
              className={`rounded-xl border p-4 cursor-pointer ${m.read ? "border-white/10 bg-[#0b0d12]" : "border-[#02AACF]/40 bg-[#0d2730]"}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white font-semibold">{m.name} <span className="text-[#64748B] text-xs font-normal">· {m.inquiry_type}</span></p>
                  <p className="text-[#64748B] text-xs">{m.email} {m.phone && `· ${m.phone}`}</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); del(m.id); }} className="text-[#64748B] hover:text-[#f87171]"><Trash2 size={15} /></button>
              </div>
              {m.subject && <p className="text-[#cbd5e1] text-sm mt-2 font-semibold">{m.subject}</p>}
              <p className="text-[#94A3B8] text-sm mt-1">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export function CalendarTab() {
  const [data, setData] = useState({ blocked: [], settings: {} });
  const [bookings, setBookings] = useState([]);
  const [month, setMonth] = useState(new Date());
  const load = () => {
    api.get("/admin/availability").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const y = month.getFullYear(), mo = month.getMonth();
  const first = new Date(y, mo, 1);
  const startPad = (first.getDay() + 6) % 7;
  const days = new Date(y, mo + 1, 0).getDate();
  const fmt = (d) => `${y}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const blockedSet = new Set(data.blocked.map((b) => b.date));
  const bookingsByDate = {};
  bookings.forEach((b) => { if (b.shoot_date && ["CONFIRMED","NEW","PENDING","RESCHEDULED","AWAITING PAYMENT"].includes(b.status)) (bookingsByDate[b.shoot_date] = bookingsByDate[b.shoot_date] || []).push(b); });

  const toggleBlock = async (ds) => {
    const existing = data.blocked.find((b) => b.date === ds);
    if (existing) await api.delete(`/admin/blocked-dates/${existing.id}`);
    else await api.post("/admin/blocked-dates", { dates: [ds], reason: "Unavailable" });
    load();
  };

  return (
    <Panel testid="calendar-tab" title="Availability Calendar" action={
      <div className="flex items-center gap-2">
        <button onClick={() => setMonth(new Date(y, mo - 1, 1))} className="text-white/60 hover:text-white px-2">‹</button>
        <span className="font-mono2 text-sm text-white w-32 text-center">{month.toLocaleString("en", { month: "long", year: "numeric" })}</span>
        <button onClick={() => setMonth(new Date(y, mo + 1, 1))} className="text-white/60 hover:text-white px-2">›</button>
      </div>
    }>
      <div className="flex gap-4 mb-4 text-xs font-mono2 flex-wrap">
        <span className="flex items-center gap-1.5 text-[#22c55e]"><span className="h-2 w-2 rounded-full bg-[#22c55e]" /> Booked</span>
        <span className="flex items-center gap-1.5 text-[#ef4444]"><span className="h-2 w-2 rounded-full bg-[#ef4444]" /> Blocked</span>
        <span className="text-[#64748B]">Click a day to block / unblock</span>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {["M","T","W","T","F","S","S"].map((d, i) => <div key={i} className="text-center text-[#64748B] text-xs font-mono2 py-1">{d}</div>)}
        {Array.from({ length: startPad }).map((_, i) => <div key={`p${i}`} />)}
        {Array.from({ length: days }).map((_, i) => {
          const d = i + 1; const ds = fmt(d);
          const isBlocked = blockedSet.has(ds); const dayBookings = bookingsByDate[ds] || [];
          return (
            <button key={d} onClick={() => toggleBlock(ds)} data-testid={`cal-day-${ds}`}
              className={`aspect-square rounded-lg border text-sm flex flex-col items-center justify-center relative transition-all ${isBlocked ? "border-[#ef4444]/50 bg-[#ef4444]/10 text-[#ef4444]" : dayBookings.length ? "border-[#22c55e]/50 bg-[#22c55e]/10 text-white" : "border-white/10 text-white/70 hover:border-[#02AACF]/50"}`}>
              {d}
              {dayBookings.length > 0 && <span className="text-[9px] font-mono2 text-[#22c55e]">{dayBookings.length}</span>}
            </button>
          );
        })}
      </div>
    </Panel>
  );
}
